/**
 * SummativeAuthoringTab.js — Sprint 2 Workstream A (v6.32.147)
 *
 * Super-Admin content-engine-driven authoring console for MOSAIC Summative.
 *
 * Operator-driven, one cell at a time:
 *   1) Pull the live gap list from /api/super-admin/coverage
 *      (filters to product_line=summative + sorted critical-first).
 *   2) Pick a gap row → click Generate → backend returns 1-3 candidates.
 *   3) Edit each candidate in place if needed; Save promotes to
 *      `questions` with product_line=summative.
 *
 * The component shows a live budget chip (today's $/$5 cap) so the
 * operator sees spend before every Generate click. ZERO mass-gen.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Save, Trash2, AlertTriangle, Loader2, RefreshCw,
  Wallet, BookOpen, Target, Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SummativeAuthoringTab() {
  const [coverage, setCoverage] = useState(null);
  const [budget, setBudget] = useState(null);
  const [selectedGap, setSelectedGap] = useState(null);
  const [count, setCount] = useState(1);
  const [bpHint, setBpHint] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [busyGen, setBusyGen] = useState(false);
  const [savedIds, setSavedIds] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Batch mode state
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, saved: 0, failed: 0, spend: 0 });
  const [batchAbort, setBatchAbort] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([
        axios.get(`${API}/super-admin/coverage?product_line=summative`),
        axios.get(`${API}/super-admin/summative/budget`),
      ]);
      setCoverage(c.data);
      setBudget(b.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const sortedGaps = useMemo(() => {
    if (!coverage?.gaps) return [];
    return [...coverage.gaps].sort((a, b) =>
      a.count - b.count || a.grade.localeCompare(b.grade)
    );
  }, [coverage]);

  const handleGenerate = async () => {
    if (!selectedGap) return;
    setBusyGen(true);
    setError(null);
    try {
      // We have to choose a modality (not part of the gap row). Default
      // to "listening" — operator can re-run with another modality from
      // the candidate-level Modality dropdown below.
      const payload = {
        grade: selectedGap.grade,
        standard: selectedGap.standard,
        klu: selectedGap.klu,
        modality: selectedGap.modality || "listening",
        count,
        bp_context_hint: bpHint.trim() || null,
      };
      const r = await axios.post(`${API}/super-admin/summative/generate`, payload);
      setCandidates((r.data?.candidates || []).map((c) => ({ ...c, _local_id: Math.random().toString(36).slice(2) })));
      setBudget((b) => ({ ...(b || {}), spent_today_usd: r.data.spent_today_usd, remaining_usd: Math.max(0, (b?.budget_usd || 0) - r.data.spent_today_usd) }));
      if (r.data?.parse_errors?.length) {
        setError(`content-engine produced ${r.data.parse_errors.length} unparseable candidate(s) — only the valid ones are shown.`);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusyGen(false);
    }
  };

  const handleSave = async (cand) => {
    setError(null);
    try {
      const { _local_id, ...payload } = cand; // eslint-disable-line no-unused-vars
      const r = await axios.post(`${API}/super-admin/summative/save`, {
        candidate: payload,
        source_gap: selectedGap,
      });
      setSavedIds((ids) => [...ids, r.data.question_id]);
      setCandidates((cs) => cs.filter((c) => c._local_id !== cand._local_id));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const handleDiscard = (cand) => {
    setCandidates((cs) => cs.filter((c) => c._local_id !== cand._local_id));
  };

  const handleBatchFillCritical = async () => {
    const criticalGaps = sortedGaps.filter((g) => g.severity === "critical").slice(0, 35);
    if (criticalGaps.length === 0) {
      setError("No critical (zero-count) gaps left to fill.");
      return;
    }
    if (!window.confirm(`Auto-fill ${criticalGaps.length} critical gaps?\n\nFor each gap: generate 1 candidate with MOSAIC content engine, validate the schema, and save to db.questions with product_line=summative.\n\nEstimated spend: ~$${(criticalGaps.length * 0.005).toFixed(2)}.\n\nProgress will be shown live.`)) return;
    setBatchAbort(false);
    setBatchRunning(true);
    setBatchProgress({ done: 0, total: criticalGaps.length, saved: 0, failed: 0, spend: 0 });
    setError(null);
    let saved = 0, failed = 0, spend = 0;
    for (let i = 0; i < criticalGaps.length; i++) {
      if (batchAbort) break;
      const g = criticalGaps[i];
      try {
        const gen = await axios.post(`${API}/super-admin/summative/generate`, {
          grade: g.grade,
          standard: g.standard,
          klu: g.klu,
          modality: g.modality || "listening",
          count: 1,
        });
        spend += gen.data?.estimated_cost_usd || 0;
        const cand = (gen.data?.candidates || [])[0];
        if (cand) {
          await axios.post(`${API}/super-admin/summative/save`, {
            candidate: cand,
            source_gap: g,
          });
          saved += 1;
        } else {
          failed += 1;
        }
      } catch (e) {
        failed += 1;
        if (e?.response?.status === 429) {
          setError(`Budget exhausted after ${saved} saves. ${e.response?.data?.detail || ""}`);
          break;
        }
      }
      setBatchProgress({ done: i + 1, total: criticalGaps.length, saved, failed, spend: Number(spend.toFixed(4)) });
    }
    setBatchRunning(false);
    if (saved > 0) {
      setSavedIds((ids) => [...ids, ...Array(saved).fill("batch")]);
    }
    await refresh();
  };

  const updateCandidateField = (id, field, value) => {
    setCandidates((cs) => cs.map((c) => (c._local_id === id ? { ...c, [field]: value } : c)));
  };

  if (loading) {
    return (
      <Card data-testid="summative-authoring-loading">
        <CardContent className="py-12 flex items-center justify-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading coverage gaps + budget…</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="summative-authoring-root">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-serif text-2xl text-mosaic-ink leading-tight">
              MOSAIC Summative Authoring (content-engine-driven)
            </h2>
            <p className="text-sm text-mosaic-ink-2">
              Pick a coverage gap → generate 1-3 candidates with MOSAIC content engine → review &amp; save.
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={refresh} data-testid="summative-authoring-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Budget chip */}
      {budget && (
        <Card data-testid="summative-budget-card">
          <CardContent className="py-4 flex items-center gap-3">
            <Wallet className={`w-5 h-5 ${budget.remaining_usd <= 0 ? "text-rose-600" : "text-emerald-700"}`} />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-mosaic-ink">
                Today's spend: ${budget.spent_today_usd?.toFixed(4)} of ${budget.budget_usd?.toFixed(2)} budget
              </div>
              <div className="text-xs text-mosaic-ink-3 mt-0.5">
                Remaining: ${budget.remaining_usd?.toFixed(4)} · Up to {budget.max_candidates_per_call} candidates per generate-click.
              </div>
            </div>
            {budget.remaining_usd <= 0 && (
              <Badge className="bg-rose-50 text-rose-700 border-rose-200">Budget exhausted</Badge>
            )}
          </CardContent>
        </Card>
      )}

      {/* Batch-mode panel */}
      <Card className="border-emerald-200" data-testid="summative-batch-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-emerald-900 uppercase tracking-wider flex items-center gap-2">
            <Zap className="w-4 h-4" /> Batch mode — auto-fill critical gaps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-mosaic-ink-2 mb-3">
            Generates one candidate per critical (zero-count) gap, validates the schema, and saves to <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">db.questions</code>. Aborts on budget cap (HTTP 429). No mass-gen — one save per cell, every save audit-logged.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleBatchFillCritical}
              disabled={batchRunning || (budget && budget.remaining_usd <= 0)}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="summative-batch-fill-btn"
            >
              {batchRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Auto-fill {sortedGaps.filter((g) => g.severity === "critical").length} critical gaps
            </Button>
            {batchRunning && (
              <Button variant="outline" size="sm" onClick={() => setBatchAbort(true)} data-testid="summative-batch-abort-btn">
                Abort
              </Button>
            )}
            {batchProgress.total > 0 && (
              <span className="text-xs font-mono text-mosaic-ink-2" data-testid="summative-batch-progress">
                {batchProgress.done}/{batchProgress.total} · saved {batchProgress.saved} · failed {batchProgress.failed} · ${batchProgress.spend.toFixed(4)}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gap picker */}
        <Card className="lg:col-span-1" data-testid="summative-gap-picker">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-mosaic-ink-2 uppercase tracking-wider flex items-center gap-2">
              <Target className="w-4 h-4" /> Coverage gaps ({sortedGaps.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[460px] overflow-y-auto divide-y divide-mosaic-border-soft" data-testid="summative-gap-list">
              {sortedGaps.slice(0, 80).map((g, i) => {
                const sel = selectedGap && selectedGap.grade === g.grade && selectedGap.standard === g.standard && selectedGap.klu === g.klu;
                return (
                  <button
                    key={`${g.grade}-${g.standard}-${g.klu}`}
                    type="button"
                    onClick={() => setSelectedGap(g)}
                    className={`w-full text-left py-2 px-2 hover:bg-mosaic-bg-subtle text-xs transition-colors ${sel ? "bg-emerald-50" : ""}`}
                    data-testid={`summative-gap-row-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-mosaic-ink">Gr {g.grade} · {g.standard_name}</span>
                      <Badge className={g.count === 0 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-900 border-amber-200"}>
                        {g.count}
                      </Badge>
                    </div>
                    <div className="text-mosaic-ink-3 mt-0.5">KLU: {g.klu}</div>
                  </button>
                );
              })}
              {sortedGaps.length === 0 && (
                <div className="text-center py-6 text-mosaic-ink-3 text-sm">No gaps — full coverage achieved.</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Generator panel */}
        <Card className="lg:col-span-2" data-testid="summative-generator">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-mosaic-ink-2 uppercase tracking-wider flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Generate candidates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedGap ? (
              <div className="text-center py-8 text-mosaic-ink-3 text-sm">Pick a coverage gap on the left to start.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                  <ReadonlyField label="Grade" value={selectedGap.grade} />
                  <ReadonlyField label="Standard" value={selectedGap.standard_name} />
                  <ReadonlyField label="KLU" value={selectedGap.klu} />
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-mosaic-ink-3">Modality</Label>
                    <select
                      value={selectedGap.modality || "listening"}
                      onChange={(e) => setSelectedGap({ ...selectedGap, modality: e.target.value })}
                      className="w-full mt-1 bg-white border border-mosaic-border-soft rounded-md px-2 py-1.5 text-mosaic-ink"
                      data-testid="summative-modality-select"
                    >
                      {["listening", "speaking", "reading", "writing"].map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-mosaic-ink-3">Count</Label>
                    <select
                      value={count}
                      onChange={(e) => setCount(parseInt(e.target.value))}
                      className="w-full mt-1 bg-white border border-mosaic-border-soft rounded-md px-2 py-1.5 text-mosaic-ink"
                      data-testid="summative-count-select"
                    >
                      {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-mosaic-ink-3">Big-picture context hint (optional)</Label>
                  <Input
                    value={bpHint}
                    onChange={(e) => setBpHint(e.target.value)}
                    placeholder='e.g., "Class farmers market", "Solar system explorers"…'
                    data-testid="summative-bp-hint"
                    className="mt-1"
                  />
                </div>
                <Button
                  onClick={handleGenerate}
                  disabled={busyGen || (budget && budget.remaining_usd <= 0)}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="summative-generate-btn"
                >
                  {busyGen ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Generate {count} candidate{count > 1 ? "s" : ""}
                </Button>
                {error && (
                  <div className="text-rose-700 text-sm flex items-start gap-2" data-testid="summative-error">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
                  </div>
                )}
                {savedIds.length > 0 && (
                  <div className="text-emerald-800 text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="summative-saved-banner">
                    ✅ Saved {savedIds.length} new question{savedIds.length > 1 ? "s" : ""} to the bank this session. Coverage Dashboard will reflect on next refresh.
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Candidates */}
      {candidates.length > 0 && (
        <div className="space-y-4" data-testid="summative-candidates">
          {candidates.map((c, idx) => (
            <CandidateCard
              key={c._local_id}
              candidate={c}
              idx={idx}
              onChange={(field, value) => updateCandidateField(c._local_id, field, value)}
              onSave={() => handleSave(c)}
              onDiscard={() => handleDiscard(c)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReadonlyField({ label, value }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-mosaic-ink-3">{label}</Label>
      <div className="mt-1 px-2 py-1.5 bg-mosaic-bg-subtle border border-mosaic-border-soft rounded-md text-mosaic-ink text-sm">
        {value}
      </div>
    </div>
  );
}

function CandidateCard({ candidate, idx, onChange, onSave, onDiscard }) {
  return (
    <Card data-testid={`summative-candidate-${idx}`} className="border-emerald-200">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-mosaic-ink uppercase tracking-wider">
          Candidate {idx + 1} — {candidate.grade_level} · {candidate.modality} · WIDA L{candidate.wida_level}
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onDiscard} data-testid={`summative-discard-${idx}`}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard
          </Button>
          <Button size="sm" onClick={onSave} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`summative-save-${idx}`}>
            <Save className="w-3.5 h-3.5 mr-1" /> Save to bank
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Field label="Big Picture Context" value={candidate.big_picture_context} onChange={(v) => onChange("big_picture_context", v)} testId={`summative-bp-${idx}`} />
        <Field label="Stimulus" value={candidate.stimulus} onChange={(v) => onChange("stimulus", v)} multi testId={`summative-stimulus-${idx}`} />
        <Field label="Question text" value={candidate.question_text} onChange={(v) => onChange("question_text", v)} multi testId={`summative-question-${idx}`} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(candidate.options || []).map((opt, i) => (
            <div key={i}>
              <Label className="text-xs text-mosaic-ink-3">Option {i + 1}{candidate.correct_answer === opt ? " ✓ correct" : ""}</Label>
              <Input
                value={opt}
                onChange={(e) => {
                  const next = [...candidate.options];
                  next[i] = e.target.value;
                  onChange("options", next);
                }}
                data-testid={`summative-option-${idx}-${i}`}
                className="mt-1"
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-mosaic-ink-3">Correct answer</Label>
            <select
              value={candidate.correct_answer}
              onChange={(e) => onChange("correct_answer", e.target.value)}
              className="w-full mt-1 bg-white border border-mosaic-border-soft rounded-md px-2 py-1.5 text-mosaic-ink"
              data-testid={`summative-correct-${idx}`}
            >
              {(candidate.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-mosaic-ink-3">Difficulty</Label>
            <select
              value={candidate.difficulty || "medium"}
              onChange={(e) => onChange("difficulty", e.target.value)}
              className="w-full mt-1 bg-white border border-mosaic-border-soft rounded-md px-2 py-1.5 text-mosaic-ink"
            >
              {["easy", "medium", "hard"].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-mosaic-ink-3">WIDA level</Label>
            <select
              value={candidate.wida_level}
              onChange={(e) => onChange("wida_level", parseInt(e.target.value))}
              className="w-full mt-1 bg-white border border-mosaic-border-soft rounded-md px-2 py-1.5 text-mosaic-ink"
            >
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>L{n}</option>)}
            </select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, onChange, multi, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-mosaic-ink-3">{label}</Label>
      {multi ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 min-h-[60px]" data-testid={testId} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testId} />
      )}
    </div>
  );
}

